import instance, { getUri } from '../index';

const RESOURCE_SHOOT = 'shoot';
const RESOURCE_ACTION = 'action';

export const ContentApi = {
  downloadRaw({ id }) {
    const uri = getUri({ url: `${RESOURCE_SHOOT}/${id}/${RESOURCE_ACTION}/download-raw` });
    window.open(uri, 'Download');
  },

  /**
   * Upload Operations
   */
  uploadBrief({ id, blob }) {
    return instance.post(`${RESOURCE_SHOOT}/${id}/${RESOURCE_ACTION}/upload-brief`, blob, {
      headers: {
        'content-type': 'application/octet-stream'
      }
    });
  },
  /**
   * Download Operations
   */
  downloadBrief({ id }) {
    const uri = getUri({ url: `${RESOURCE_SHOOT}/${id}/${RESOURCE_ACTION}/download-brief` });
    window.open(uri, 'Download');
  },

  uploadRawsPreview({ id, blob, onUploadProgress }) {
    return instance.post(`${RESOURCE_SHOOT}/${id}/${RESOURCE_ACTION}/upload-raws-preview`, blob, {
      headers: {
        'Content-Type': 'application/octet-stream'
      },
      onUploadProgress
    });
  },

  /**
   * Multipart Upload Operations
   */
  initializeMultipartUpload({ id }) {
    return instance.post(`${RESOURCE_SHOOT}/${id}/${RESOURCE_ACTION}/init-multipart-upload`);
  },

  getUploadPartUrl({ id, uploadId, partNumber, key }) {
    return instance.get(`${RESOURCE_SHOOT}/${id}/${RESOURCE_ACTION}/upload-part-url`, {
      params: { uploadId, partNumber, key }
    });
  },

  completeMultipartUpload({ id, uploadId, key, parts }) {
    return instance.post(`${RESOURCE_SHOOT}/${id}/${RESOURCE_ACTION}/complete-multipart-upload`, { uploadId, key, parts });
  },

  abortMultipartUpload({ id, uploadId, key }) {
    return instance.post(`${RESOURCE_SHOOT}/${id}/${RESOURCE_ACTION}/abort-multipart-upload`, { uploadId, key });
  },

  /**
   * Presigned URL Operations
   */
  getPresignedUrls({ id, files }) {
    return instance.post(`${RESOURCE_SHOOT}/${id}/${RESOURCE_ACTION}/get-presigned-urls`, { files });
  },

  /**
   * Image and Video Operations
   */
  addContent({ id, payload }) {
    return instance.post(`${RESOURCE_SHOOT}/${id}/${RESOURCE_ACTION}/add-content`, payload);
  },

  createVideosBatch({ id, payload }) {
    return instance.post(`${RESOURCE_SHOOT}/${id}/${RESOURCE_ACTION}/add-content`, payload);
  },

  updateImageStatus({ id, payload }) {
    return instance.post(`/image/${id}/action/update-status`, payload);
  },

  updateRawImageStatus({ id, payload }) {
    return instance.post(`/raw/${id}/action/update-status`, payload);
  },

  updateImageMetadata({ content, id, payload }) {
    return instance.post(`/${content}/${id}/action/update-metadata`, payload);
  },

  bulkApproveImages({ payload }) {
    return instance.post('/image/action/bulk-approve', payload);
  },

  approveRemainingImages({ payload }) {
    return instance.post('/image/action/approve-remaining', payload);
  },

  bulkSelectRaws({ payload }) {
    return instance.post('/raw/action/bulk-select', payload);
  },

  updatePreview({ id, payload }) {
    return instance.post(`/shoot/${id}/action/update-preview`, payload);
  },

  updateRawsPreview({ id, payload }) {
    return instance.post(`/shoot/${id}/action/update-raws-preview`, payload);
  },

  deleteImage({ content, id }) {
    return instance.delete(`/${content}/${id}/action/delete`);
  },

  /**
   * Image Search and Format Operations
   */
  getContent({ filters = {}, pagination = { limit: 20, cursor: null } } = {}) {
    return instance
      .post('/content/search', {
        filters,
        pagination
      })
      .then(response => ({
        content: response.data.content,
        pagination: response.data.pagination
      }));
  },

  // TODO: Remove this feature when we implement the social export feature
  exportImages({ imageIds, formatOptions }) {
    return instance
      .post('/images/format', {
        imageIds,
        formatOptions: {
          width: formatOptions.width,
          height: formatOptions.height,
          fit: formatOptions.fit,
          format: formatOptions.format,
          quality: formatOptions.quality,
          preserveAspectRatio: formatOptions.preserveAspectRatio,
          watermark: formatOptions.watermark
        }
      })
      .then(response => response.data.images);
  },

  /**
   * Download Job Operations
   */
  enqueueDownloadJob({ id }) {
    return instance.post(`/${RESOURCE_SHOOT}/${id}/${RESOURCE_ACTION}/enqueue-download-job`).then(response => {
      return {
        shootId: response.data?.shootId,
        jobId: response.data?.jobId,
        status: response.data?.status || 'pending'
      };
    });
  },

  getDownloadJobStatus({ id }) {
    return instance.get(`/${RESOURCE_SHOOT}/${id}/${RESOURCE_ACTION}/enqueue-download-job/status`).then(response => {
      return {
        status: response.data?.status || 'pending',
        lastUpdated: new Date(response.data?.lastUpdated),
        progress: response.data?.progress,
        message: response.data?.message,
        startedAt: response.data?.startedAt ? new Date(response.data?.startedAt) : null,
        shootId: response.data?.shootId,
        downloadUrl: response.data?.downloadUrl || null
      };
    });
  },

  /**
   * Social Content Operations
   */
  generateHashtags({ caption }) {
    return instance.post('/social-content/generate-hashtags', { caption }).then(response => response.data.hashtags);
  },

  improveCaption({ caption }) {
    return instance.post('/social-content/improve-caption', { caption }).then(response => response.data.improvedCaption);
  },

  getSignedCookie() {
    return instance.post('/contents/signed-cookies', { url: 'https://cdn.app.flashy-staging.ae/323/e72c8aa4-dae3-495b-bd1f-54932950c851/hls/stream.m3u8' }).then(response => {
      return response.data;
    });
  },

  getVideos() {
    return instance.get('/videos').then(response => {
      return response.data;
    });
  },

  /**
   * Check processing status for multiple videos
   * @param {Array<number>} videoIds - Array of video IDs to check status
   * @returns {Promise} Promise containing the processing status for each video
   */
  getVideosProcessingStatus(videoIds) {
    return instance.post('/videos/processing-status', { videoIds });
  },

  /**
   * Add a label to an image
   * @param {string} id - The ID of the image
   * @param {string} label - The label to add
   * @returns {Promise<Array<string>>} Updated labels array
   */
  addLabelToImage({ id, label }) {
    return instance.post(`/images/${id}/labels`, { label }).then(response => response.data.labels);
  },

  /**
   * Remove a label from an image
   * @param {string} id - The ID of the image
   * @param {string} label - The label to remove
   * @returns {Promise<Array<string>>} Updated labels array
   */
  removeLabelFromImage({ id, label }) {
    return instance.delete(`/images/${id}/labels`, { data: { label } }).then(response => response.data.labels);
  },

  /**
   * Add a comment to an image
   * @param {string} id - The ID of the image
   * @param {string} content - The comment content
   * @returns {Promise<Object>} The created comment object
   */
  addCommentToImage({ id, content }) {
    return instance.post(`/images/${id}/add-comment`, { content }).then(response => response.data.comment);
  }
};
